import FieldType from "@/utils/constants/FieldType.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import dayjs, {ISO_DATE_FORMATS} from "@/utils/dayjs";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import QuestionField = namaTypes.QuestionField;

export default class CrossFilterRenderer {
  static renderValue(filledQuestion: DTOFilledQuestionField | undefined, questionField: QuestionField | undefined): string {
    const value = filledQuestion?.value;
    if (ObjectChecker.isEmptyOrNull(value))
      return "";
    switch (questionField?.fieldType) {
      case FieldType.Reference:
      case FieldType.Genericreference:
        return CrossFilterRenderer.renderReference(value);
      case FieldType.Enum:
        return CrossFilterRenderer.renderEnum(value, questionField?.metaData?.enumTypeName);
      case FieldType.Boolean:
        return CrossFilterRenderer.renderBoolean(value);
      case FieldType.Date:
        return DateFieldUtils.dateToString(dayjs(value, ISO_DATE_FORMATS).toDate());
      case FieldType.DateAndTime:
        return DateFieldUtils.dateAndTimeToString(dayjs(value, ISO_DATE_FORMATS).toDate());
      case FieldType.Time:
        return DateFieldUtils.timeToString(value);
      case FieldType.Decimal:
        return CrossFilterRenderer.renderDecimal(value);
      case FieldType.Integer:
      case FieldType.Long:
        return CrossFilterRenderer.renderNumeric(value);
      default:
        return value ?? "";
    }
  }

  static renderLabel(questionField: QuestionField | undefined): string {
    const id = questionField?.id;
    if (!id) return "";
    return Translator.translateId(id);
  }

  static iconForType(fieldType: string | undefined): string | undefined {
    switch (fieldType) {
      case FieldType.Reference:
      case FieldType.Genericreference:
        return "link";
      case FieldType.Enum:
        return "list";
      case FieldType.Boolean:
        return "check_box";
      case FieldType.Date:
      case FieldType.DateAndTime:
        return "event";
      case FieldType.Time:
        return "schedule";
      case FieldType.Decimal:
      case FieldType.Integer:
      case FieldType.Long:
        return "tag";
      default:
        return undefined;
    }
  }

  private static renderReference(value: string): string {
    const parsed = FieldTypesUtils.parseString(value, FieldType.Reference);
    if (!parsed || typeof parsed !== "object") return value;
    return Translator.translateRef(parsed) || value;
  }

  private static renderEnum(value: string, enumTypeName: string | undefined): string {
    if (enumTypeName)
      return Translator.translateId(enumTypeName + "." + value) || value;
    const translatedValues = [];
    value.split(CommonConstants.LIST_PARAMS_SEP).forEach(v => translatedValues.push(Translator.translateId(v)));
    return StringUtils.toCSVLineWithSep(",", translatedValues);
  }

  private static renderBoolean(value: string): string {
    if (value === "true" || value === "1") return Translator.translateId("yes") ?? "";
    if (value === "false" || value === "0") return Translator.translateId("no") ?? "";
    return value;
  }

  private static renderDecimal(value: string): string {
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4});
  }

  private static renderNumeric(value: string): string {
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toLocaleString();
  }
}
